/**
 * The subset of `RTCStatsReport` entries this package actually reads. `RTCStatsReport`
 * (lib.dom.d.ts) types entries as `any` — the real report contains many more entry
 * `type`s (outbound-rtp, remote-candidate, codec, ...) that both consumers of this
 * union (`connection-quality.ts`, `teleop-session/media/rtp-stats.ts`) already silently
 * skip in their `.forEach`. The open `{ type: string }` arm keeps that skip-unknown
 * behavior instead of narrowing the union closed and rejecting entries neither file
 * inspects.
 */

export interface InboundRtpVideoStats {
  type: "inbound-rtp";
  kind: "video";
  id: string;
  timestamp?: number;
  packetsLost?: number;
  packetsReceived?: number;
  framesDropped?: number;
  bytesReceived?: number;
  jitterBufferDelay?: number;
  jitterBufferEmittedCount?: number;
  totalDecodeTime?: number;
  framesDecoded?: number;
}

export interface CandidatePairStats {
  type: "candidate-pair";
  state: string;
  nominated?: boolean;
  currentRoundTripTime?: number;
}

export type RtcStatsEntry = InboundRtpVideoStats | CandidatePairStats | { type: string };

/**
 * `r.type === "inbound-rtp"` alone does NOT narrow `RtcStatsEntry` — the open
 * `{ type: string }` fallback arm's discriminant is the general `string` type, which
 * overlaps every literal, so TS can't rule it out on an equality check alone. Checking
 * for a field only the specific arm declares (`"kind" in r`) narrows correctly instead.
 */
export function isInboundRtpVideoStats(r: RtcStatsEntry): r is InboundRtpVideoStats {
  return r.type === "inbound-rtp" && "kind" in r && r.kind === "video";
}

export function isCandidatePairStats(r: RtcStatsEntry): r is CandidatePairStats {
  return r.type === "candidate-pair" && "state" in r;
}
